'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useAppDescription } from '@/app/hooks/useAppDescription';
import { iconForApp } from '@/app/features/workspace/utils/appIcon';
import TileStarButton from './TileStarButton';
import type { UserAppAccessSummaryDto } from '@/app/services/api/userAppAccessService';

/**
 * Mobile-only horizontal-scroll "Favorites" strip at the top of MobileWorkspaceHome.
 * Hidden entirely when the user has zero favorites. Matches iOS Files Favorites /
 * Notion mobile / Shopify admin. Sprint 2.3 workspace-mobile-favorites.
 */

interface FavoritesSectionProps {
  favorites: UserAppAccessSummaryDto[];
  isLoading: boolean;
  onTileClick: (app: UserAppAccessSummaryDto) => void;
}

const FavoritesSection: React.FC<FavoritesSectionProps> = React.memo(
  ({ favorites, isLoading, onTileClick }) => {
    const { t } = useTranslation('workspace');
    const { getAppText } = useAppDescription();

    if (!isLoading && favorites.length === 0) {
      return null;
    }

    return (
      <section aria-label={t('favorites.heading')} className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t('favorites.heading')}
        </h2>
        <div
          className={cn(
            '-mx-4 px-4 flex gap-3 overflow-x-auto',
            'snap-x snap-mandatory scroll-smooth',
            '[&::-webkit-scrollbar]:hidden'
          )}
          style={{ scrollbarWidth: 'none' }}
          aria-busy={isLoading}
        >
          {isLoading && favorites.length === 0
            ? [0, 1, 2].map((i) => (
                <div
                  key={`skeleton-${i}`}
                  className="h-24 w-20 flex-none rounded-xl bg-muted animate-pulse snap-start"
                  aria-hidden
                />
              ))
            : favorites.map((app) => {
                const text = getAppText(app.appCode, app.appName, app.description, app.id);
                const Icon = iconForApp(app);
                return (
                  <div key={app.id} className="relative flex-none snap-start">
                    <button
                      type="button"
                      onClick={() => onTileClick(app)}
                      className={cn(
                        'touch-target',
                        'h-24 w-20 flex flex-col items-center justify-center gap-1 p-2',
                        'rounded-xl border border-border/60 bg-card',
                        'hover:bg-accent/40 active:bg-accent/60 transition-colors'
                      )}
                    >
                      <Icon className="w-6 h-6 text-primary" />
                      <span className="text-xs font-medium text-center line-clamp-2 w-full leading-tight">
                        {text.name}
                      </span>
                    </button>
                    <TileStarButton sysAppId={app.id} />
                  </div>
                );
              })}
        </div>
      </section>
    );
  }
);

FavoritesSection.displayName = 'FavoritesSection';
export default FavoritesSection;
